import { useCallback, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface Citation {
  title: string;
  source: string | null;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
  /** True while the assistant placeholder is awaiting a reply. */
  pending?: boolean;
  /** True if this assistant turn failed to produce a reply. */
  error?: boolean;
}

interface ChatApiResponse {
  ok?: boolean;
  reply?: string;
  citations?: Citation[];
  usedContext?: boolean;
  error?: string;
}

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * useSupportChat — drives the RAG support chat. Holds the message history in
 * component state and posts the whole conversation to /kb-chat with the
 * caller's Supabase bearer token on each send.
 */
export function useSupportChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const inFlight = useRef(false);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || inFlight.current) return;
      inFlight.current = true;
      setSending(true);

      const userMsg: ChatMessage = { id: newId(), role: 'user', content: trimmed };
      const assistantId = newId();

      // Snapshot the conversation we send to the API (history + new user turn),
      // excluding the pending placeholder.
      const apiMessages = [...messages, userMsg].map((m) => ({
        role: m.role,
        content: m.content,
      }));

      setMessages((prev) => [
        ...prev,
        userMsg,
        { id: assistantId, role: 'assistant', content: '', pending: true },
      ]);

      try {
        const { data: session } = await supabase.auth.getSession();
        const jwt = session.session?.access_token;
        if (!jwt) throw new Error('Your session expired — please sign in again.');

        const res = await fetch('/.netlify/functions/kb-chat', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ messages: apiMessages }),
        });

        let data: ChatApiResponse | null = null;
        const raw = await res.text();
        if (raw && raw.trim().length > 0) {
          try {
            data = JSON.parse(raw) as ChatApiResponse;
          } catch {
            data = null;
          }
        }

        const reply = data?.reply?.trim();
        if (reply) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    content: reply,
                    citations: data?.citations ?? [],
                    pending: false,
                    error: res.ok ? false : true,
                  }
                : m,
            ),
          );
        } else {
          const msg =
            data?.error ??
            (res.ok ? 'No response received.' : `Request failed (HTTP ${res.status}).`);
          throw new Error(msg);
        }
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Something went wrong. Please try again or contact support.';
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  content: `Sorry — ${message}`,
                  pending: false,
                  error: true,
                }
              : m,
          ),
        );
      } finally {
        inFlight.current = false;
        setSending(false);
      }
    },
    [messages],
  );

  const reset = useCallback(() => {
    setMessages([]);
  }, []);

  return { messages, sending, send, reset };
}
